import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function ModSla() {
  const { lang } = useLang();
  const [cats, setCats] = useState([]);
  const [defaultHours, setDefaultHours] = useState(24);
  const [perCat, setPerCat] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/sla")]).then(([c, s]) => {
      setCats(c.data);
      setDefaultHours(s.data.default_hours || 24);
      setPerCat(s.data.categories || {});
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(perCat).forEach(([k, v]) => { if (v !== "" && Number(v) > 0) clean[k] = Number(v); });
      await api.put("/sla", { default_hours: Number(defaultHours) || 24, categories: clean });
      toast.success("Saved");
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  if (loading) return <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div>;

  return (
    <div className="max-w-2xl">
      <div className="rounded-lg border border-zinc-800/70 bg-[#111113] p-5">
        <div className="mb-4 flex items-center gap-2 text-[13px] font-semibold text-zinc-100">
          <Clock size={16} className="text-blue-400" /> Review deadlines (hours)
        </div>
        <div className="mb-5 flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
          <span className="text-[13px] text-zinc-300">Default (all categories)</span>
          <Input data-testid="sla-default" type="number" min="1" value={defaultHours} onChange={(e) => setDefaultHours(e.target.value)} className="h-9 w-24 border-zinc-800 bg-zinc-900 text-[13px]" />
        </div>
        <div className="space-y-2">
          {cats.map((c) => (
            <div key={c.key} className="flex items-center justify-between rounded-md border border-zinc-800/70 px-4 py-2.5">
              <span className="text-[13px] text-zinc-300">{c[lang] || c.en}</span>
              <Input
                data-testid={`sla-${c.key}`}
                type="number"
                min="1"
                placeholder={String(defaultHours)}
                value={perCat[c.key] ?? ""}
                onChange={(e) => setPerCat({ ...perCat, [c.key]: e.target.value })}
                className="h-9 w-24 border-zinc-800 bg-zinc-950 text-[13px]"
              />
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-zinc-600">Leave a category blank to use the default deadline.</p>
        <Button data-testid="sla-save" onClick={save} disabled={saving} className="mt-4 bg-blue-600 text-white hover:bg-blue-700">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </Button>
      </div>
    </div>
  );
}
